/**
 * VendorProtectedRoute - Protège les routes vendeur (POS, Stocks, etc.)
 *
 * Logique de sécurité :
 * 1. Vérifie si un admin/manager est connecté (token JWT valide) → accès direct
 * 2. Sinon, vérifie si une session vendeur est active dans le store
 * 3. Si aucune des deux conditions, redirige vers /login
 *
 * Une fois authentifié (admin OU vendeur sélectionné), pas de reconnexion nécessaire
 * pendant la session de travail.
 */
import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePOSStore } from '../hooks/usePOSStore';
import CaisseTactile from './pos/CaisseTactile';

interface VendorProtectedRouteProps {
  children: ReactNode;
  requireVendorSelection?: boolean; // Si true, exige qu'un vendeur soit sélectionné
}

export default function VendorProtectedRoute({
  children,
  requireVendorSelection = false,
}: VendorProtectedRouteProps) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const vendorId = usePOSStore((state) => state.vendorId);
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    // Attendre que le contexte d'auth soit chargé
    if (!authLoading) {
      setIsValidating(false);
    }
  }, [authLoading]);

  // Afficher un loader pendant la validation
  if (isValidating || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Vérification de l'accès...</p>
        </div>
      </div>
    );
  }

  // Cas 1: Admin/Manager connecté avec JWT → accès autorisé
  if (isAuthenticated) {
    // Si on exige une sélection de vendeur et qu'il n'y en a pas, laisser le composant gérer
    // (POSWorkspace affiche VendorQuickBar dans ce cas)
    return <CaisseTactile>{children}</CaisseTactile>;
  }

  // Cas 2: Pas d'admin connecté mais vendeur sélectionné dans le store
  // Cela permet aux vendeurs de continuer à travailler après sélection initiale
  if (vendorId && !requireVendorSelection) {
    return <CaisseTactile>{children}</CaisseTactile>;
  }

  // Cas 3: Aucune authentification → rediriger vers login
  // On sauvegarde l'URL de destination pour y revenir après connexion
  return <Navigate to="/login" state={{ from: location }} replace />;
}
